import lang from "translations";
import {
  formatName,
  prepareChart,
  denormalizePlotDateTimeFromApiResponse,
  getApiDateTimeFormatByTimeUnit,
  getDateTimeFormatByTimeUnit,
  formatNumberWithComma,
  formatNumberToOrdinal,
  timeAgo,
  prettifyTransactionType,
  prettifyPaymentMethod,
  isFailedTransaction,
} from "services";
import { formatNumberToMoneyWithCurrencySymbol } from "services/money.service";
import * as moment from "moment";
import {
  dateTimeRequest,
  dateToTimeAgo,
  filterRequest,
  selectToObjectRequest,
} from "./common.mapper";
import {
  DateTime,
  ExternalPaymentStatus,
  ExternalPaymentVoidStatus,
  PaymentMethod,
  PaymentType,
  TimeUnit,
  TransactionStatus,
  TransactionType,
} from "enums";
import { add, subtract, multiply, divide, toAmount } from "services/number.service";
import { getCurrentTimeWithTimezone } from "./common.mapper";
import { Text } from "components";
import { calcQuarterDataGraph, calcWeekDataGraph } from "services/chart.service";

const isTransactionVoided = (transaction = {}) => {
  return Boolean(
    transaction.externalPaymentVoidStatus ||
    transaction.transactionStatus === TransactionStatus.VOID ||
    (transaction.type === TransactionType.REDEEM &&
      transaction.transactionStatus === TransactionStatus.EDITED) ||
    transaction.voidAuthorizationProfileId ||
    transaction.voidAuthorizationTagUid,
  );
};

export const transactionListResponse = {
  transactionId: { key: "transactionId" },
  deviceUpdatedDate: { key: "deviceUpdatedDate" },
  dateCreated: dateToTimeAgo("deviceUpdatedDate"),
  guestId: { key: "guestId" },
  guestName: {
    transform: ({ src }) => {
      var name = formatName(src.userFirstName, src.userLastName);
      if (!name && src.guestId) {
        name = `Guest ${src.guestId ? src.guestId : ""}`.trim();
      }
      return name;
    },
  },
  userTagUid: { key: "userTagUid" },
  userTagUseCount: {
    transform: ({ src }) => {
      return lang.populate(lang.nthUse, [formatNumberToOrdinal(src.userTagUseCount)]);
    },
  },
  locationName: { key: "locationName" },
  locationId: { key: "locationId" },
  tableName: { key: "tableName" },
  staffName: {
    transform: ({ src }) => {
      return formatName(src.staffFirstName, src.staffLastName);
    },
  },
  staffTagUid: { key: "staffTagUid" },
  staffProfileId: { key: "staffProfileId" },
  transactionStatus: { key: "transactionStatus" },
  transactionType: { key: "type" },
  paymentType: { key: "paymentType" },
  tapStatus: { key: "nfcStatus" },
  paidAmount: {
    transform: ({ self }) => {
      return formatNumberToMoneyWithCurrencySymbol(self);
    },
  },
  items: {
    transform: ({ src }) => {
      if (src.paymentMethod && src.type === TransactionType.TOPUP) {
        return [
          {
            name: prettifyPaymentMethod(src.paymentMethod, src.dynamicPaymentMethod),
            deleted: false,
          },
        ];
      }
      if (src?.transactionItems) {
        return src.transactionItems.map((item) => {
          const variants = item.productVariants?.length
            ? item.productVariants?.map((variant) => {
                return variant.value;
              })
            : [];

          if (variants?.length) {
            return {
              name: `${item.productName} · ${variants.join(" · ")}`,
              deleted: item.productSkuDeleted,
            };
          }

          return { name: item.productName, deleted: item.productSkuDeleted };
        });
      }
      return [];
    },
  },
  qty: {
    transform: ({ src }) => {
      let qty = 0;
      if (src?.transactionItems.length) {
        src.transactionItems.forEach((item) => {
          qty = qty + item.quantity;
        });
        return toAmount(qty, "0,0");
      }

      return "";
    },
  },
  paymentMethod: {
    key: "paymentMethod",
  },
  isCheckout: {
    key: "isCheckout",
  },
  externalPaymentStatus: {
    key: "externalPaymentStatus",
  },
  isVoided: {
    transform: ({ src }) => {
      return isTransactionVoided(src);
    },
  },
  isFailed: {
    transform: ({ src }) => {
      return isFailedTransaction(
        src.externalPaymentVoidStatus ? src.externalPaymentVoidStatus : src.externalPaymentStatus,
        src.nfcStatus,
      );
    },
  },
};

export const transactionListFilterRequest = {
  ...filterRequest,
  ...dateTimeRequest,
  orders: {
    transform: ({ src }) => {
      return { [`t.deviceCreatedDate`]: src.sort.value };
    },
  },
  locationIds: selectToObjectRequest("locations"),
  staffIds: selectToObjectRequest("staffs"),
  shiftIds: {
    transform: ({ src }) => {
      if (src.shifts.length > 0) {
        return src.shifts.split(",").map((s) => parseInt(s));
      }
      return [];
    },
  },
  transactionStatus: { key: "transactionStatus" },
  transactionTypes: {
    transform: ({ self }) => {
      if (!self || self.length === 0 || self[0] === undefined) {
        return [
          TransactionType.SALE,
          TransactionType.TOPUP,
          TransactionType.RETURN,
          TransactionType.REDEEM,
          TransactionType.MIXED,
          TransactionType.ISSUE_FOC,
          TransactionType.REMOVE_FOC,
          TransactionType.VOUCHER_ISSUE,
          TransactionType.VOUCHER_REMOVE,
        ];
      } else {
        return self.map((transactionType) => {
          return transactionType.value;
        });
      }
    },
  },
  nfcStatus: { key: "tapStatus" },
  paymentType: { key: "paymentType" },
  externalPaymentStatus: {
    transform: ({ src }) => {
      if (!src.externalPaymentStatus || !src.externalPaymentStatus.length) return [];
      return src.externalPaymentStatus.map(({ value }) => value);
    },
  },
};

export const graphLineChartOfTotalSale = {
  data: {
    transform: (arg) => {
      const { src, params } = arg;
      const plot = denormalizePlotDateTimeFromApiResponse(src.plot, params.interval);
      return prepareChart(plot);
    },
  },
  total: {
    transform: ({ src }) => {
      return formatNumberToMoneyWithCurrencySymbol(src.totalSales);
    },
  },
  _keys: ["totalSales"],
};

export const graphMultiLineChartOfTotalSale = {
  data: {
    transform: ({ src, params }) => {
      let labels = [];
      const data = src.locations.map((location) => {
        const plot = prepareChart(
          denormalizePlotDateTimeFromApiResponse(location.plot, params.interval),
        );
        labels = plot.labels;
        return {
          label: location.location.locationName,
          ...plot,
        };
      });
      return {
        data,
        labels,
      };
    },
  },
  total: {
    transform: ({ src }) => {
      let total = 0;
      src.locations.forEach((location) => {
        total = location.totalSales + total;
      });
      return formatNumberToMoneyWithCurrencySymbol(total);
    },
  },
  totalSale: {
    transform: ({ src }) => {
      let total = 0;
      src.locations.forEach((location) => {
        total = location.totalSales + total;
      });
      return total;
    },
  },
};

export const salesReportListRequest = {
  ...filterRequest,
  ...dateTimeRequest,
  totalSales: { key: "totalSales" },
  requestedAt: getCurrentTimeWithTimezone(),
};

const selectValues = (key) => ({
  transform: ({ src }) => {
    const value = src[key];
    if (!Array.isArray(value)) return [];
    return value.map((item) => (typeof item === "object" ? item.value : item));
  },
});

export const salesReportRequest = {
  venueId: { key: "venueId" },
  startDateTime: { key: "startDateTime" },
  endDateTime: { key: "endDateTime" },
  locationIds: selectValues("locations"),
  categoryIds: selectValues("categories"),
  productIds: selectValues("items"),
  paymentMethodIds: selectValues("paymentMethods"),
  staffProfileIds: selectValues("staffs"),
};

export const salesReportDetailsRequest = {
  ...salesReportRequest,
  filterBy: { key: "filterBy" },
};

export const salesReportOverviewResponse = {
  totalSales: { key: "totalSales" },
  itemsSold: { key: "itemsSold" },
  averageOrderValue: { key: "averageOrderValue" },
  unSpentCredit: { key: "unSpentCredit" },
  paymentMethodDetailResponses: {
    transform: ({ self }) => self || [],
  },
  paymentFlowDetailResponse: {
    transform: ({ self }) => self || {},
  },
};

export const salesReportDetailsResponse = {
  overTimeDetailResponses: {
    transform: ({ self }) => self || [],
  },
  objectDetailResponses: {
    transform: ({ self }) => self || [],
  },
  peakHourDetailResponses: {
    transform: ({ self }) => self || [],
  },
};

export const graphPieChartOfTopLocationSales = {
  name: {
    transform: ({ src }) => {
      return src.locationName;
    },
  },
  value: {
    transform: ({ src }) => {
      return src.totalSale;
    },
  },
  _keys: ["totalSale"],
};

const barSetting = {
  CASH: {
    label: lang.cash,
    color: "#0D121F",
  },
  CREDIT: {
    label: lang.card,
    color: "#E0F2F1",
  },
  CREDIT_DEBIT: {
    label: lang.card,
    color: "#5FB3B5",
  },
  OTHERS: {
    label: lang.others,
    color: "#EE99AE",
  },
};

const barSettingTotalSales = {
  SALES_CREDITS: {
    label: lang.credits,
    color: "#E0F2F1",
  },
  SALES_FREE_CREDITS: {
    label: lang.freeCredits,
    color: "#EE99AE",
  },
  SALES_CASH: {
    label: lang.cash,
    color: "#0D121F",
  },
  SALES_CREDIT_DEBIT: {
    label: lang.creditDebit,
    color: "#5FB3B5",
  },
  E_WALLET: {
    label: lang.eWallet,
    color: "#9DC9C8",
  },
  SALES_OTHERS: {
    label: lang.others,
    color: "#DEEEFC",
  },
};

export const graphTotalSales = {
  sales: { key: "sales" },
  salesCash: { key: "salesCash" },
  salesCreditDebit: { key: "salesCreditDebit" },
  salesCredits: { key: "salesCredits" },
  salesFreeCredits: { key: "salesFreeCredits" },
  salesOthers: { key: "salesOthers" },
  grossProfit: { key: "grossProfit" },
  supplyPrice: { key: "supplyPrice" },
  taxes: { key: "taxes" },
  discounts: { key: "discounts" },
  voidedItems: { key: "voidedItems" },
  voidedValue: { key: "voidedValue" },
  itemsSold: { key: "itemsSold" },
  barChartData: {
    transform: ({ src }) => {
      let credits = [
        {
          label: barSettingTotalSales.SALES_CREDITS.label,
          color: barSettingTotalSales.SALES_CREDITS.color,
          value: src.salesCredits,
        },
        {
          label: barSettingTotalSales.SALES_FREE_CREDITS.label,
          color: barSettingTotalSales.SALES_FREE_CREDITS.color,
          value: src.salesFreeCredits,
        },
        {
          label: barSettingTotalSales.SALES_CASH.label,
          color: barSettingTotalSales.SALES_CASH.color,
          value: src.salesCash,
        },
        {
          label: barSettingTotalSales.SALES_CREDIT_DEBIT.label,
          color: barSettingTotalSales.SALES_CREDIT_DEBIT.color,
          value: src.salesCreditDebit,
        },
        {
          label: barSettingTotalSales.E_WALLET.label,
          color: barSettingTotalSales.E_WALLET.color,
          value: src.salesEWallet,
        },
        {
          label: barSettingTotalSales.SALES_OTHERS.label,
          color: barSettingTotalSales.SALES_OTHERS.color,
          value: src.salesOthers,
        },
      ];
      return credits;
    },
  },
};

export const barStackChartTotalSales = {
  data: {
    transform: ({ src, params }) => {
      let creditSalesPlot = [];
      let freeCreditSalesPlot = [];
      let cashSalesPlot = [];
      let creditDebitSalesPlot = [];
      let ewalletSalesPlot = [];
      let otherSales = [];

      src.plot.forEach((plot) => {
        const keys = Object.keys(plot);
        keys.forEach((key, index) => {
          creditSalesPlot.push({
            [key]: plot[key].creditSales,
          });
          freeCreditSalesPlot.push({
            [key]: plot[key].freeCreditSales,
          });
          cashSalesPlot.push({
            [key]: plot[key].cashSales,
          });
          creditDebitSalesPlot.push({
            [key]: plot[key].creditDebitSales,
          });
          ewalletSalesPlot.push({ [key]: plot[key].ewalletSales });
          otherSales.push({
            [key]: plot[key].otherSales,
          });
        });
      });

      const chartForCreditSales = prepareChart(
        denormalizePlotDateTimeFromApiResponse(creditSalesPlot, params.splitType),
      );
      const chartForFreeCreditSales = prepareChart(
        denormalizePlotDateTimeFromApiResponse(freeCreditSalesPlot, params.splitType),
      );
      const chartForCashSales = prepareChart(
        denormalizePlotDateTimeFromApiResponse(cashSalesPlot, params.splitType),
      );
      const chartForCreditDebitSales = prepareChart(
        denormalizePlotDateTimeFromApiResponse(creditDebitSalesPlot, params.splitType),
      );
      const chartForEWalletSales = prepareChart(
        denormalizePlotDateTimeFromApiResponse(ewalletSalesPlot, params.splitType),
      );
      const chartForOtherSales = prepareChart(
        denormalizePlotDateTimeFromApiResponse(otherSales, params.splitType),
      );
      let labels = chartForCreditSales.labels;
      return {
        data: [
          { ...chartForCreditSales, label: lang.credits },
          { ...chartForFreeCreditSales, label: lang.freeCredits },
          { ...chartForCashSales, label: lang.cashSales },
          { ...chartForCreditDebitSales, label: lang.creditDebitSales },
          { ...chartForEWalletSales, label: lang.eWallet },
          { ...chartForOtherSales, label: lang.others },
        ],
        labels,
      };
    },
  },
  creditSales: {
    transform: ({ src }) => {
      return {
        text: formatNumberToMoneyWithCurrencySymbol(src.creditSales),
        value: src.creditSales,
      };
    },
  },
  freeCreditSales: {
    transform: ({ src }) => {
      return {
        text: formatNumberToMoneyWithCurrencySymbol(src.freeCreditSales),
        value: src.freeCreditSales,
      };
    },
  },
  cashSales: {
    transform: ({ src }) => {
      return {
        text: formatNumberToMoneyWithCurrencySymbol(src.cashSales),
        value: src.cashSales,
      };
    },
  },
  creditDebitSales: {
    transform: ({ src }) => {
      return {
        text: formatNumberToMoneyWithCurrencySymbol(src.creditDebitSales),
        value: src.creditDebitSales,
      };
    },
  },
  otherSales: {
    transform: ({ src }) => {
      return {
        text: formatNumberToMoneyWithCurrencySymbol(src.otherSales),
        value: src.otherSales,
      };
    },
  },
};

export const graphTopUpTotal = {
  total: {
    key: "totalAmount",
  },
  barChartData: {
    transform: ({ src }) => {
      let topUps = [];
      const others = {
        value: 0,
        label: barSetting.OTHERS.label,
        color: barSetting.OTHERS.color,
      };
      src.topUpTotals?.forEach((total) => {
        const t = barSetting[total.paymentMethod];
        if (["CREDIT", "CASH", "CREDIT_DEBIT"].includes(total.paymentMethod)) {
          topUps.push({
            label: t.label,
            color: t.color,
            value: total.amount,
          });
        } else {
          others.value = others.value + Number(total.amount);
        }
      });
      topUps = others.value ? [...topUps, others] : topUps;
      return topUps;
    },
  },
  lineChartData: {
    transform: ({ src, params }) => {
      let plot = src.topUpPlots?.map((tu) => {
        const items = tu.paymentMethods
          ?.map((pm) => {
            return {
              name: ["CREDIT", "CASH", "CREDIT_DEBIT"].includes(pm.paymentMethod)
                ? barSetting[pm.paymentMethod]?.label
                : barSetting.OTHERS.label,
              value: pm.amount,
            };
          })
          .filter(({ value }) => {
            return Boolean(value);
          });
        if (tu) {
          return {
            timestamp: tu.timestamp,
            name: moment(tu.timestamp, getApiDateTimeFormatByTimeUnit(params.splitType)).format(
              getDateTimeFormatByTimeUnit(params.splitType),
            ),
            value: tu.totalAmount,
            items,
          };
        }
        return null;
      });
      if (params.interval === TimeUnit.Week || params.interval === TimeUnit.WeekS) {
        plot = calcWeekDataGraph(plot, params.interval === TimeUnit.WeekS);
      } else if (params.interval === TimeUnit.Quarter) {
        plot = calcQuarterDataGraph(plot);
      }
      return prepareChart(plot);
    },
  },
};

export const graphLineChartOfTotalCheckIns = {
  data: {
    transform: ({ src, params }) => {
      const plot = denormalizePlotDateTimeFromApiResponse(
        src.plot.map((p) => {
          const key = Object.keys(p)[0];
          return {
            [key]: p[key].newGuest,
          };
        }),
        params.interval,
      );

      return prepareChart(plot);
    },
  },
  total: {
    transform: ({ src }) => {
      return {
        text: formatNumberWithComma(src.totalGuest),
        value: src.totalGuest,
      };
    },
  },
};

export const graphLineChartOfTotalReturns = {
  data: {
    transform: ({ src, params }) => {
      const plot = denormalizePlotDateTimeFromApiResponse(src.plot, params.interval);
      return prepareChart(plot);
    },
  },
  total: {
    transform: ({ src }) => {
      return {
        text: formatNumberToMoneyWithCurrencySymbol(src.totalSales),
        value: src.totalSales,
      };
    },
  },
};

export const viewTransactions = {
  _keys: [
    "location",
    "table",
    "type",
    "voidReason",
    "note",
    "nfcStatus",
    "externalPaymentStatus",
    "externalPaymentVoidStatus",
    "updatedFromTransactionId",
    "newTransactionId",
  ],
  transactionDate: {
    transform: ({ src }) => {
      return timeAgo(src.deviceUpdatedDate);
    },
  },
  isVoided: {
    transform: ({ src }) => {
      return isTransactionVoided(src);
    },
  },
  dynamicPaymentMethod: {
    key: "dynamicPaymentMethod",
  },
  paymentMethod: {
    key: "paymentMethod",
  },
  paymentType: {
    key: "paymentType",
  },
  isCheckout: {
    key: "isCheckout",
  },
  isFailed: {
    transform: ({ src }) => {
      return isFailedTransaction(
        src.externalPaymentVoidStatus ? src.externalPaymentVoidStatus : src.externalPaymentStatus,
        src.nfcStatus,
      );
    },
  },
  registerInfo: {
    transform: ({ src }) => {
      const { deviceName, deviceSerialNumber } = src.device || {};
      return {
        shiftId: src.shiftId,
        deviceImei: src.deviceImei,
        deviceMacAddress: src.deviceMacAddress,
        deviceSerialNumber: deviceSerialNumber,
        deviceName,
      };
    },
  },
  guest: {
    transform: ({ src }) => {
      const { guest = {}, userTagUseCount = 0, userTagUid } = src;
      const { firstName, lastName, email, dob, phone, phoneCountryCode } = guest || {};
      const guestName = formatName(firstName, lastName);
      const name =
        guestName ||
        (userTagUseCount
          ? lang.populate(lang.nthGuestWhoUseThisTag, [formatNumberToOrdinal(userTagUseCount)])
          : "Unavailable");

      let postpaidCreditLimit = null;
      guest?.guestCheckins?.forEach((guestCheckin) => {
        guestCheckin.guestTags?.forEach((guestTag) => {
          postpaidCreditLimit = guestTag.postpaidCreditLimit;
        });
      });

      return {
        name,
        postpaidCreditLimit,
        tagUid: userTagUid,
        hasName: Boolean(guestName),
        email,
        phone: `${phoneCountryCode ? `+${phoneCountryCode.replace("+", "")}` : ""} ${
          phone || ""
        }`.trim(),
        dob: dob ? moment(dob).format(DateTime.A) : "",
      };
    },
  },
  previousBalance: {
    transform: ({ src }) => {
      let previousBalance = 0;
      let credits = src.userTagPreviousBalance;
      let freeCredits = src.userTagPreviousFreeCredits;

      previousBalance = credits;

      let postPaidCreditsUsed = 0;
      if (credits < 0) {
        postPaidCreditsUsed = Math.abs(credits);
      }

      return [
        [
          lang.previousBalance,
          formatNumberToMoneyWithCurrencySymbol(previousBalance),
          { strongTitle: false },
          "mt-md",
          Boolean(src.nfcStatus === "FAILED")
            ? "mt-md text-gray-300"
            : previousBalance < 0
              ? "mt-md text-red"
              : "mt-md text-black",
        ],
        [
          lang.credits,
          formatNumberToMoneyWithCurrencySymbol(credits < 0 ? 0 : credits),
          {},
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
        [
          lang.freeCredits,
          formatNumberToMoneyWithCurrencySymbol(freeCredits),
          {},
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
        [
          lang.postPaidCreditsUsed,
          formatNumberToMoneyWithCurrencySymbol(postPaidCreditsUsed),
          {},
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
      ];
    },
  },
  newBalance: {
    transform: ({ src }) => {
      let previousBalance = 0;
      let credits = src.userTagNewBalance;
      let freeCredits = src.userTagNewFreeCredits;

      if (credits >= 0) {
        previousBalance = credits + freeCredits;
      } else {
        previousBalance = credits;
      }

      let postPaidCreditsUsed = 0;
      if (credits < 0) {
        postPaidCreditsUsed = Math.abs(credits);
      }

      return [
        [
          lang.guestsNewBalance,
          formatNumberToMoneyWithCurrencySymbol(previousBalance),
          { strongTitle: true },
          "mt-md font-semibold",
          Boolean(src.nfcStatus === "FAILED")
            ? "mt-md text-md text-gray-300"
            : previousBalance < 0
              ? "mt-md text-md text-red"
              : "mt-md text-md text-black",
        ],
        [
          lang.credits,
          formatNumberToMoneyWithCurrencySymbol(credits < 0 ? 0 : credits),
          {},
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
        [
          lang.freeCredits,
          formatNumberToMoneyWithCurrencySymbol(freeCredits),
          {},
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
        [
          lang.postPaidCreditsUsed,
          formatNumberToMoneyWithCurrencySymbol(postPaidCreditsUsed),
          { superscript: src.type === TransactionType.ISSUE_FOC ? "1" : null },
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
      ];
    },
  },
  nfcStatus: {
    transform: ({ src }) => {
      return src.nfcStatus;
    },
  },
  totalPaid: {
    transform: ({ src }) => {
      const isReturn = src.type === TransactionType.RETURN;
      const paymentMethod = src.paymentMethod;
      let label = isReturn
        ? lang.totalReturnedCredits
        : src.nfcStatus === "FAILED" && src.transactionStatus !== "VOID"
          ? lang.total
          : lang.totalPaid;
      if (src.type === TransactionType.ISSUE_FOC) {
        label = lang.totalFreeCreditsIssued;
      } else if (src.type === TransactionType.REMOVE_FOC) {
        label = lang.totalFreeCreditsRemoved;
      }

      let items = [
        [
          label,
          formatNumberToMoneyWithCurrencySymbol(src.paidAmount),
          {
            fontSize: "text-lg",
            danger: isReturn,
            strong: true,
            lineThrough: src.externalPaymentVoidStatus
              ? src.externalPaymentVoidStatus === ExternalPaymentVoidStatus.Success
              : Boolean(
                  src.transactionStatus === "VOID" &&
                  (src.voidAuthorizationProfileId || src.voidDeviceDate),
                ),
          },
          "mt-md font-semibold text-md",
          Boolean(src.nfcStatus === "FAILED")
            ? "mt-md text-gray-300 font-semibold"
            : "mt-md text-black font-semibold",
        ],
      ];

      let remainingAmount = src.paidAmount;
      let paidCredits = 0;
      let paidFreeCredits = 0;
      let paidPostpaidCredits = 0;
      const totalAdditionalCharge = src.transactionAdditionalCharges?.reduce(
        (acc, curr) => (acc += curr.chargedAmount),
        0,
      );

      if (remainingAmount > 0 && src.userTagPreviousFreeCredits > 0) {
        if (src.userTagPreviousFreeCredits > remainingAmount) {
          paidFreeCredits = remainingAmount - (totalAdditionalCharge + src.tipAmount);
        } else {
          paidFreeCredits = src.userTagPreviousFreeCredits;
        }
        remainingAmount = remainingAmount - paidFreeCredits;
      }

      if (remainingAmount > 0) {
        if (src.userTagPreviousBalance <= 0) {
          paidCredits = 0;
          paidPostpaidCredits = remainingAmount;
        } else {
          if (src.userTagPreviousBalance > remainingAmount) {
            paidCredits = remainingAmount;
            paidPostpaidCredits = 0;
          } else {
            paidCredits = src.userTagPreviousBalance;
            remainingAmount = remainingAmount - paidCredits;
            paidPostpaidCredits = remainingAmount;
          }
        }
      }

      const isPaidByCashOrCard = [
        PaymentMethod.Cash,
        PaymentMethod.CreditDebit,
        PaymentMethod.Credit,
        PaymentMethod.EWallet,
        PaymentMethod.Others,
      ].includes(paymentMethod);

      if (
        (src.type === TransactionType.SALE || src.type === TransactionType.MIXED) &&
        !isPaidByCashOrCard
      ) {
        items.push([
          lang.credits,
          formatNumberToMoneyWithCurrencySymbol(paidCredits),
          {},
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ]);

        items.push([
          lang.freeCredits,
          formatNumberToMoneyWithCurrencySymbol(paidFreeCredits),
          {},
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ]);

        items.push([
          lang.postPaidCreditsUsed,
          formatNumberToMoneyWithCurrencySymbol(paidPostpaidCredits),
          {},
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ]);
      }

      if (src.type === TransactionType.SALE && paymentMethod === PaymentMethod.Cash) {
        let totalCashPaid = src.paidAmount + src.cashChange;
        items.push([
          lang.cash,
          formatNumberToMoneyWithCurrencySymbol(totalCashPaid),
          "text-black",
          "text-black",
        ]);

        items.push([
          lang.change,
          formatNumberToMoneyWithCurrencySymbol(src.cashChange),
          "text-black",
          "text-black",
        ]);
      }

      if (
        src.type === TransactionType.SALE &&
        (paymentMethod === PaymentMethod.CreditDebit ||
          paymentMethod === PaymentMethod.Credit ||
          paymentMethod === PaymentMethod.Others)
      ) {
        items.push([
          prettifyPaymentMethod(src.paymentMethod, src.dynamicPaymentMethod),
          !src.externalPaymentStatus || src.externalPaymentStatus === ExternalPaymentStatus.Success
            ? formatNumberToMoneyWithCurrencySymbol(src.paidAmount)
            : "-",
          "text-black",
          "text-black",
        ]);
        if (src.externalPaymentStatus) {
          items.push([
            lang.cardNumber,
            src.cardNumber ? src.cardNumber : "-",
            "text-black",
            "text-black",
          ]);
          items.push([
            lang.invoiceNumber,
            src.ghlInvoiceNumber ? src.ghlInvoiceNumber : "-",
            "text-black",
            "text-black",
          ]);
          items.push([
            lang.cardApprovalNumber,
            src.cardApprovalNumber ? src.cardApprovalNumber : "-",
            "text-black",
            "text-black",
          ]);
        } else {
          items.push([
            lang.referenceNo,
            src.referenceNumber ? src.referenceNumber : "-",
            "text-black",
            "text-black",
          ]);
        }
      }
      if (
        src.type === TransactionType.TOPUP &&
        (paymentMethod === PaymentMethod.CreditDebit || paymentMethod === PaymentMethod.Credit)
      ) {
        if (src.externalPaymentStatus) {
          items.push([
            prettifyPaymentMethod(src.paymentMethod, src.dynamicPaymentMethod),
            src.externalPaymentStatus === ExternalPaymentStatus.Success
              ? formatNumberToMoneyWithCurrencySymbol(src.paidAmount)
              : "-",
            "text-black",
            "text-black",
          ]);
          items.push([
            lang.invoiceNumber,
            src.ghlInvoiceNumber ? src.ghlInvoiceNumber : "-",
            "text-black",
            "text-black",
          ]);
          items.push([
            lang.cardApprovalNumber,
            src.cardApprovalNumber ? src.cardApprovalNumber : "-",
            "text-black",
            "text-black",
          ]);
        }
      }
      if (
        (src.type === TransactionType.SALE || src.type === TransactionType.TOPUP) &&
        paymentMethod === PaymentMethod.EWallet
      ) {
        items.push([
          src.tradeType ? src.tradeType : lang.eWallet,
          src.externalPaymentStatus === ExternalPaymentStatus.Success
            ? formatNumberToMoneyWithCurrencySymbol(src.paidAmount)
            : "-",
          "text-black",
          "text-black",
        ]);
        items.push([
          lang.populate(lang.tradeTypeTransactionNumber, [
            src.tradeType ? src.tradeType : lang.eWallet,
          ]),
          src.tradeTransactionNumber ? src.tradeTransactionNumber : "-",
          "text-black",
          "text-black",
        ]);
        items.push([
          lang.invoiceNumber,
          src.ghlInvoiceNumber ? src.ghlInvoiceNumber : "-",
          "text-black",
          "text-black",
        ]);
      }

      return items;
    },
  },
  voidTotalPaid: {
    transform: ({ src }) => {
      const isReturn = src.type === TransactionType.RETURN;
      const paymentMethod = src.paymentMethod;
      const isPaidByCashOrCard = [
        PaymentMethod.Cash,
        PaymentMethod.CreditDebit,
        PaymentMethod.Credit,
        PaymentMethod.EWallet,
        PaymentMethod.Others,
      ].includes(paymentMethod);
      let label = isReturn
        ? lang.totalReturnedCredits
        : src.nfcStatus === "FAILED"
          ? lang.total
          : lang.totalReturnedCredits;
      if (src.type === TransactionType.ISSUE_FOC) {
        label = lang.totalFreeCreditsIssued;
      } else if (src.type === TransactionType.REMOVE_FOC) {
        label = lang.totalFreeCreditsRemoved;
      } else if (src.type === TransactionType.SALE && isPaidByCashOrCard) {
        label = lang.totalRefunded;
      }

      let items = [
        [
          label,
          formatNumberToMoneyWithCurrencySymbol(src.paidAmount),
          {
            fontSize: "text-lg",
            danger: isReturn,
            strong: true,
            lineThrough: Boolean(src.voidAuthorizationProfileId),
          },
          "mt-md font-semibold text-md",
          Boolean(src.nfcStatus === "FAILED")
            ? "mt-md text-gray-300 font-semibold"
            : "mt-md text-black font-semibold",
        ],
      ];

      if (src.type === TransactionType.SALE && paymentMethod === PaymentMethod.Cash) {
        let totalCashPaid =
          src.transactionStatus === "VOID" ? src.paidAmount : src.paidAmount + src.cashChange;
        items.push([
          lang.cash,
          formatNumberToMoneyWithCurrencySymbol(totalCashPaid),
          "text-black",
          "text-black",
        ]);
      }

      if (
        src.type === TransactionType.SALE &&
        (paymentMethod === PaymentMethod.CreditDebit ||
          paymentMethod === PaymentMethod.Credit ||
          paymentMethod === PaymentMethod.Others)
      ) {
        items.push([
          paymentMethod === PaymentMethod.Others ? lang.others : lang.creditDebit,
          formatNumberToMoneyWithCurrencySymbol(src.paidAmount),
          "text-black",
          "text-black",
        ]);
        items.push([
          lang.referenceNo,
          src.voidReferenceNumber ? src.voidReferenceNumber : "-",
          "text-black",
          "text-black",
        ]);
      }

      return items;
    },
  },
  voidedNewBalance: {
    transform: ({ src }) => {
      let postPaidCreditsUsed = 0;
      if (src.voidUserTagNewBalance < 0) {
        postPaidCreditsUsed = Math.abs(src.voidUserTagNewBalance);
      }

      let credits = src.voidUserTagNewBalance;

      return [
        [
          lang.guestsNewBalance,
          formatNumberToMoneyWithCurrencySymbol(credits),
          "mt-md text-black-600 font-semibold",
          Boolean(src.nfcStatus === "FAILED")
            ? "mt-md text-gray-300 text-md font-semibold"
            : credits < 0
              ? "mt-md text-red text-md font-semibold"
              : "mt-md text-black text-md font-semibold",
        ],
        [
          lang.credits,
          formatNumberToMoneyWithCurrencySymbol(
            src.voidUserTagNewBalance > 0 ? src.voidUserTagNewBalance : 0,
          ),
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
        [
          lang.freeCredits,
          formatNumberToMoneyWithCurrencySymbol(
            src.voidUserTagNewFreeCredits < 0 ? 0 : src.voidUserTagNewFreeCredits,
          ),
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
        [
          lang.postPaidCreditsUsed,
          formatNumberToMoneyWithCurrencySymbol(postPaidCreditsUsed),
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
      ];
    },
  },
  voidedPreviousBalance: {
    transform: ({ src }) => {
      let postPaidCreditsUsed = 0;
      if (src.voidUserTagPreviousBalance < 0) {
        postPaidCreditsUsed = Math.abs(src.voidUserTagPreviousBalance);
      }

      let credits = src.voidUserTagPreviousBalance;

      return [
        [
          lang.previousBalance,
          formatNumberToMoneyWithCurrencySymbol(credits),
          "mt-md text-black-600",
          Boolean(src.nfcStatus === "FAILED")
            ? "mt-md text-gray-300 font-semibold"
            : credits < 0
              ? "mt-md text-red font-semibold"
              : "mt-md text-black font-semibold",
        ],
        [
          lang.credits,
          formatNumberToMoneyWithCurrencySymbol(
            src.voidUserTagPreviousBalance > 0 ? src.voidUserTagPreviousBalance : 0,
          ),
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
        [
          lang.freeCredits,
          formatNumberToMoneyWithCurrencySymbol(src.voidUserTagPreviousFreeCredits),
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
        [
          lang.postPaidCreditsUsed,
          formatNumberToMoneyWithCurrencySymbol(postPaidCreditsUsed),
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
      ];
    },
  },
  hasItems: {
    transform: ({ src }) => {
      return Boolean(src.transactionItems?.length);
    },
  },
  items: {
    transform: ({ src }) => {
      let row = [];
      const taxes = {};
      const tip = {};
      let totalItems = 0;
      let totalDeductions = {};
      let totalTaxAmount = 0;
      let quantity = 0;
      let amount = 0;
      const cartDiscount = {};
      const itemDiscounts = [];
      const type = src.type;

      if ([TransactionType.TOPUP, TransactionType.RETURN].includes(src.type)) {
        const isReturn = src.type === TransactionType.RETURN;
        row.push({
          type: "item",
          name: isReturn ? lang.credits : lang.topupCredits,
          info: isReturn ? "" : prettifyPaymentMethod(src.paymentMethod, src.dynamicPaymentMethod),
          amount: formatNumberToMoneyWithCurrencySymbol(src.paidAmount),
        });
      }
      if (src.type === TransactionType.ISSUE_FOC) {
        row.push({
          type: "item",
          name: lang.freeCredits,
          amount: formatNumberToMoneyWithCurrencySymbol(
            src.userTagNewFreeCredits - src.userTagPreviousFreeCredits,
          ),
          size: "text-lg",
        });
      } else if (src.type === TransactionType.REMOVE_FOC) {
        row.push({
          type: "item",
          name: lang.freeCredits,
          amount: formatNumberToMoneyWithCurrencySymbol(
            src.userTagPreviousFreeCredits - src.userTagNewFreeCredits,
          ),
          size: "text-lg",
        });
      }

      if (src.tipAmount) {
        tip.amount = src.tipAmount;
        if (src.tipId) {
          tip.percentage = src.tip.value;
        }
      }

      let calculatedSubTotal = 0;
      let calculatedOriginalSubTotal = 0;
      src.transactionItems.forEach((transaction) => {
        let totalPrice = src.isSiigoTransaction
          ? add(transaction.totalAmount, transaction.additionalChargeAmount)
          : transaction.totalAmount;
        const deductions = [];
        if (transaction.transactionVoucherItem) {
          const voucher = transaction.transactionVoucherItem;
          voucher.name = `${voucher.quantity} x ${voucher.itemName}`;
          deductions.push({ label: lang.voucher, value: voucher.name });
          const voucherAmount = multiply(transaction.retailPrice, voucher.quantity);
          totalDeductions.voucher =
            totalDeductions && totalDeductions.voucher
              ? add(totalDeductions.voucher, voucherAmount)
              : voucherAmount;
          // re calculate total price in case of non siigo transaction
          if (!src.isSiigoTransaction) {
            totalPrice = subtract(totalPrice, voucherAmount);
          }
        }

        if (transaction.discountAmount) {
          const discount = {
            discountName: transaction.discountName || "-",
            discountId: transaction.discountId,
            discountIdiscountAmountd: transaction.discountId,
            discountAmount: divide(transaction.discountAmount, transaction.discountQuantity),
            totalDiscountAmount: transaction.discountAmount,
            discountPercentage: transaction.discountPercentage,
            discountType: transaction.discountType,
            discountQuantity: transaction.discountQuantity,
            discountAmountRaw: transaction.discountAmount,
          };
          if (transaction.transactionDiscountType === "CART_DISCOUNT") {
            cartDiscount[discount.discountId] = {
              ...discount,
              totalQuantity: cartDiscount[discount.discountId]
                ? add(cartDiscount[discount.discountId].discountQuantity, discount.discountQuantity)
                : discount.discountQuantity,
            };
          } else if (transaction.transactionDiscountType === "ITEM_DISCOUNT") {
            let existingDiscount = itemDiscounts.find(
              (d) =>
                d.discountId === discount.discountId && d.discountName === discount.discountName,
            );
            if (existingDiscount) {
              if (existingDiscount.discountAmountRaw < discount.discountAmountRaw) {
                existingDiscount.discountAmountRaw = discount.discountAmountRaw;
              }
              if (existingDiscount.discountPercentage < discount.discountPercentage) {
                existingDiscount.discountPercentage = discount.discountPercentage;
              }
              existingDiscount.totalDiscountAmount = add(
                existingDiscount.totalDiscountAmount,
                discount.totalDiscountAmount,
              );
            } else {
              itemDiscounts.push(discount);
            }
          } else {
            deductions.push({
              label: lang.discount,
              value: `${discount.discountName} ${
                discount.discountType === "PERCENTAGE"
                  ? `(${discount.discountPercentage}%)`
                  : `(${formatNumberToMoneyWithCurrencySymbol(discount.discountAmount)} off)`
              }`,
              text: `( ${discount.discountQuantity} x ${formatNumberToMoneyWithCurrencySymbol(
                discount.discountAmount,
              )} )`,
            });
          }
          totalPrice = subtract(totalPrice, discount.totalDiscountAmount);
        }

        const itemRedeem = transaction.transactionVoucherItem
          ? transaction.quantity === transaction.transactionVoucherItem.quantity
          : false;
        if (transaction.taxId && type !== TransactionType.Redeemed && !itemRedeem) {
          const tax = {
            taxId: transaction.taxId,
            taxName: transaction.taxName,
            taxAmount: transaction.taxAmount,
            taxPercentage: transaction.taxPercentage,
          };
          deductions.push({
            label: lang.tax,
            value: `${tax.taxName} (${tax.taxPercentage}%)`,
            text: formatNumberToMoneyWithCurrencySymbol(tax.taxAmount),
          });
          totalTaxAmount = (Number(totalTaxAmount) + Number(tax.taxAmount)).toFixed(2);
          taxes[tax.taxId] = taxes[tax.taxId]
            ? {
                ...taxes[tax.taxId],
                amount: tax.taxAmount + taxes[tax.taxId].amount,
              }
            : {
                rate: tax.taxPercentage,
                amount: tax.taxAmount,
                name: tax.taxName,
              };
        }

        totalItems = add(totalItems, transaction.quantity);
        quantity = quantity + transaction.quantity;
        amount = amount + transaction.retailPrice;
        // keep subtotal in sync with what each item row displays:
        // net = the price shown, original = the struck-through price
        calculatedSubTotal = add(calculatedSubTotal, totalPrice < 0 ? 0 : totalPrice);
        calculatedOriginalSubTotal = add(calculatedOriginalSubTotal, transaction.totalAmount);
        row.push({
          type: "productItem",
          product: {
            productId: transaction.productId,
            productName: transaction.productName || "",
            productVariants: transaction.productVariants,
            info: transaction.productSkuDeleted ? `(${lang.deleted})` : "",
            isDeleted: transaction.productSkuDeleted,
          },
          quantity: `${transaction.quantity} x ${formatNumberToMoneyWithCurrencySymbol(
            transaction.bargainPrice != null ? transaction.bargainPrice : transaction.retailPrice,
          )}`,

          total: formatNumberToMoneyWithCurrencySymbol(totalPrice < 0 ? 0 : totalPrice),
          originalPrice:
            totalPrice !== transaction.totalAmount
              ? formatNumberToMoneyWithCurrencySymbol(transaction.totalAmount)
              : null,
          originalBargainPrice:
            transaction.bargainPrice != null
              ? formatNumberToMoneyWithCurrencySymbol(transaction.retailPrice)
              : null,
          bargainPrice: formatNumberToMoneyWithCurrencySymbol(transaction.bargainPrice),
          bargain: transaction.bargainPrice != null,
          deductions,
          hasDiscounts: transaction.discountAmount > 0,
          discounts: {
            text: transaction.discountQuantity ? lang.discount : "",
            prices: transaction.discountQuantity
              ? `${
                  transaction.discountType === "PERCENTAGE"
                    ? `${transaction.discountPercentage}%`
                    : `${formatNumberToMoneyWithCurrencySymbol(
                        divide(transaction.discountAmount, transaction.discountQuantity),
                      )}`
                }`
              : "",
            quantity: transaction.discountQuantity ? transaction.discountQuantity : "",
            discountName: transaction.discountName,
            percentage: transaction.discountQuantity
              ? `${
                  transaction.discountType === "PERCENTAGE"
                    ? `(${transaction.discountPercentage}%)`
                    : `(${formatNumberToMoneyWithCurrencySymbol(
                        divide(transaction.discountAmount, transaction.discountQuantity),
                      )})`
                }`
              : "",
            quantityTimesPrice: transaction.discountQuantity
              ? `(${transaction.discountQuantity} x ${
                  transaction.discountType === "PERCENTAGE"
                    ? `${transaction.discountPercentage}%`
                    : `${formatNumberToMoneyWithCurrencySymbol(
                        divide(transaction.discountAmount, transaction.discountQuantity),
                      )}`
                })`
              : "",
            discountAmountRaw: transaction.discountQuantity
              ? `${
                  transaction.discountType === "PERCENTAGE"
                    ? `(${transaction.discountPercentage}%)`
                    : `(${formatNumberToMoneyWithCurrencySymbol(transaction.discountAmount)})`
                }`
              : "",
          },
          note: transaction.note,
        });
      });

      const summaryPrices = [
        {
          value: formatNumberToMoneyWithCurrencySymbol(calculatedSubTotal),
        },
      ];

      if (calculatedOriginalSubTotal !== calculatedSubTotal) {
        summaryPrices.push({
          value: formatNumberToMoneyWithCurrencySymbol(calculatedOriginalSubTotal),
          deduction: true,
          lineThrough: true,
        });
      }

      row.push({
        type: "summary",
        text: lang.subTotal,
        quantity: ` ${quantity} item${quantity > 1 ? "s" : ""}`,
        prices: summaryPrices,
        classNames: "font-semibold",
      });

      if (src.cartDiscount) {
        const d = src.cartDiscount;
        row.push({
          type: "summary",
          text: `${lang.discount} ${
            d.discountType === "PERCENTAGE"
              ? `(${d.discountPercentage}%)`
              : `(${formatNumberToMoneyWithCurrencySymbol(d.discountAmount)} off)`
          }`,
          description:
            `${d.discountName} (${formatNumberToMoneyWithCurrencySymbol(d.discountAmount)})` || "-",
          prices: [
            {
              value: `- ${formatNumberToMoneyWithCurrencySymbol(d.discountAmount)}`,
              discount: true,
            },
          ],
        });
      }

      itemDiscounts.forEach((d) => {
        row.push({
          type: "summary",
          text: `${lang.discount} ${
            d.discountType === "PERCENTAGE"
              ? `(${d.discountPercentage}%)`
              : `(${formatNumberToMoneyWithCurrencySymbol(d.discountAmountRaw)} off)`
          }`,
          description: `${d.discountName}` || "-",
          prices: [
            {
              value: `(${formatNumberToMoneyWithCurrencySymbol(d.totalDiscountAmount)})`,
            },
          ],
        });
      });

      if (src.transactionAdditionalCharges && src.transactionAdditionalCharges.length > 0) {
        for (let addCharge of src.transactionAdditionalCharges) {
          row.push({
            type: "additionalCharge",
            text: `${addCharge.name} (${
              addCharge.type === "FIXED"
                ? `${formatNumberToMoneyWithCurrencySymbol(addCharge.value)}`
                : `${addCharge.value}%`
            })`,
            amount: `${formatNumberToMoneyWithCurrencySymbol(addCharge.chargedAmount)}`,
            classNames: "font-semibold",
          });
        }
      }

      row.push({
        type: "summary",
        text: lang.total,
        prices: [
          {
            value: formatNumberToMoneyWithCurrencySymbol(src.paidAmount),
          },
        ],
        classNames: "font-semibold",
      });

      if (Object.keys(taxes)?.length) {
        const totalTaxCount = Object.keys(taxes).length;
        row.push({
          type: "tax",
          text: lang.tax,
          description: `${totalTaxCount} Tax${totalTaxCount > 1 ? "es" : ""}`,
          value: formatNumberToMoneyWithCurrencySymbol(totalTaxAmount),
          taxes: Object.keys(taxes).map((tax) => {
            const t = taxes[tax];
            return {
              name: "",
              taxName: `${t.name} (${t.rate}%)`,
              rate: formatNumberToMoneyWithCurrencySymbol(t.amount),
            };
          }),
          classNames: "font-semibold",
        });
      }

      if (Object.keys(tip)?.length) {
        row.push({
          type: "tip",
          text: lang.tip,
          value: formatNumberToMoneyWithCurrencySymbol(tip.amount),
          percentage: tip.percentage ? ` (${tip.percentage}%)` : "",
          classNames: "font-semibold",
        });
      }

      // if (src.paymentType === PaymentType.Split) {
      // const splitCount = (src.transactionPaymentDetails || []).length || 1;
      // const splitShareAmount = (src.originalAmount + (src.tipAmount || 0)) / splitCount;
      // row.push({
      //   type: "summary",
      //   text: lang.splitShare,
      //   prices: [
      //     {
      //       value: formatNumberToMoneyWithCurrencySymbol(splitShareAmount),
      //     },
      //   ],
      //   classNames: "font-semibold",
      // });
      // }

      return row;
    },
  },
  historyTable: {
    transform: ({ src }) => {
      const { type, paymentMethod, externalPaymentStatus, externalPaymentVoidStatus } = src || {};
      const isVoided = isTransactionVoided(src);
      const idFailed = isFailedTransaction(externalPaymentStatus, src.nfcStatus);
      const data = [];
      const isPaidByCashOrCard = [
        PaymentMethod.Cash,
        PaymentMethod.CreditDebit,
        PaymentMethod.Credit,
        PaymentMethod.Others,
        PaymentMethod.EWallet,
      ].includes(paymentMethod);
      if (isVoided) {
        const {
          voidUserTagTapCount = 0,
          voidDeviceDate = null,
          voidStaffTagUid,
          voidAuthorizationTagUid = null,
          voidStaff = {},
          voidAuthorization = {},
        } = src;
        const { firstName, lastName } = voidStaff || {};
        const { firstName: authFirstName, lastName: authLastName } = voidAuthorization || {};
        const dateTime = timeAgo(voidDeviceDate, true);
        data.push({
          tapCount:
            isPaidByCashOrCard && src.type === TransactionType.SALE ? (
              <Text color="text-gray">{lang.none}</Text>
            ) : (
              formatNumberToOrdinal(voidUserTagTapCount)
            ),
          dateTime: {
            date: dateTime.date || "-",
            time: dateTime.time || "-",
          },
          staff: {
            name: !isPaidByCashOrCard
              ? formatName(firstName, lastName)
              : formatName(authFirstName, authLastName) || "-",
            tagUid: !isPaidByCashOrCard ? voidStaffTagUid : voidAuthorizationTagUid,
          },
          authorization: {
            name: formatName(authFirstName, authLastName) || "-",
            tagUid: voidAuthorizationTagUid,
          },
          status:
            (isFailedTransaction(externalPaymentVoidStatus, src.nfcStatus) ? lang.failed : "") +
            " " +
            prettifyTransactionType(src.type) +
            " " +
            lang.voided,
        });
      }
      const { userTagTapCount = 0, deviceCreatedDate, staff = {}, staffTagUid } = src;
      const { firstName, lastName } = staff || {};
      const d = timeAgo(deviceCreatedDate, true);
      const dateTime = {
        date: d.date || "-",
        time: d.time || "-",
      };
      const staffName = {
        name: formatName(firstName, lastName) || "-",
        tagUid: staffTagUid,
      };
      const historySale = {
        tapCount:
          isPaidByCashOrCard && src.type === TransactionType.SALE ? (
            <Text color="text-gray">{lang.none}</Text>
          ) : (
            formatNumberToOrdinal(userTagTapCount)
          ),
        dateTime,
        staff: staffName,
      };
      if (type === TransactionType.MIXED) {
        data.push({
          ...historySale,
          status:
            (idFailed ? lang.failed : "") + " " + prettifyTransactionType(TransactionType.SALE),
        });
        data.push({
          ...historySale,
          status:
            (idFailed ? lang.failed : "") + " " + prettifyTransactionType(TransactionType.REDEEM),
        });
      } else {
        data.push({
          ...historySale,
          status: (idFailed ? lang.failed : "") + " " + prettifyTransactionType(type),
        });
      }

      return {
        data,
        columns: [
          {
            text: lang.tapCount,
            key: "tapCount",
          },
          {
            text: lang.date,
            key: "dateTime",
            custom: true,
          },
          {
            text: lang.staff,
            key: "staff",
            custom: true,
          },
          ...(isVoided
            ? [
                {
                  text: lang.voidAuthorization,
                  key: "authorization",
                  custom: true,
                },
              ]
            : []),
          {
            text: lang.type,
            key: "status",
          },
        ],
      };
    },
  },
  splitInfo: {
    transform: ({ src }) => {
      if (src.paymentType !== PaymentType.Split) return null;
      const paymentDetails = src.transactionPaymentDetails || [];
      const sortedDetails = [...paymentDetails].sort(
        (a, b) => (a.paymentOrder || 0) - (b.paymentOrder || 0),
      );

      return sortedDetails.map((detail) => {
        const paidFreeCredits = Number(detail.paidFreeCredits || 0);

        return {
          guestName: detail.guestName || "-",
          paidAmount: formatNumberToMoneyWithCurrencySymbol(detail.paidAmount),
          paidFreeCredits:
            paidFreeCredits > 0 ? formatNumberToMoneyWithCurrencySymbol(paidFreeCredits) : null,
          vouchers: (detail.vouchers || []).map((voucher) => ({
            label: `${voucher.quantity}x ${voucher.voucherName}`,
            amount: formatNumberToMoneyWithCurrencySymbol(voucher.voucherAmount),
          })),
        };
      });
    },
  },
  paymentDetailsTable: {
    transform: ({ src }) => {
      const { type, paymentMethod, externalPaymentStatus, externalPaymentVoidStatus } = src || {};
      const isVoided = isTransactionVoided(src);
      const idFailed = isFailedTransaction(externalPaymentStatus, src.nfcStatus);
      const isPaidByCashOrCard = [
        PaymentMethod.Cash,
        PaymentMethod.CreditDebit,
        PaymentMethod.Credit,
        PaymentMethod.Others,
        PaymentMethod.EWallet,
      ].includes(paymentMethod);
      const { deviceCreatedDate, staff = {}, staffTagUid } = src;
      const { firstName, lastName } = staff || {};
      const d = timeAgo(deviceCreatedDate, true);
      const dateTime = {
        date: d.date || "-",
        time: d.time || "-",
      };
      const staffRow = {
        name: formatName(firstName, lastName) || "-",
        tagUid: staffTagUid,
      };
      const paymentDetails = src.transactionPaymentDetails || [];
      const sortedDetails = [...paymentDetails].sort(
        (a, b) => (a.paymentOrder || 0) - (b.paymentOrder || 0),
      );
      const data = [];

      if (isVoided) {
        const {
          voidDeviceDate = null,
          voidStaffTagUid,
          voidAuthorizationTagUid = null,
          voidStaff = {},
          voidAuthorization = {},
        } = src;
        const { firstName: voidFirstName, lastName: voidLastName } = voidStaff || {};
        const { firstName: authFirstName, lastName: authLastName } = voidAuthorization || {};
        const voidDateTime = timeAgo(voidDeviceDate, true);

        data.push({
          paymentOrder: "-",
          dateTime: {
            date: voidDateTime.date || "-",
            time: voidDateTime.time || "-",
          },
          staff: {
            name: !isPaidByCashOrCard
              ? formatName(voidFirstName, voidLastName)
              : formatName(authFirstName, authLastName) || "-",
            tagUid: !isPaidByCashOrCard ? voidStaffTagUid : voidAuthorizationTagUid,
          },
          guest: {
            name: "-",
            tagUid: null,
          },
          status:
            (isFailedTransaction(externalPaymentVoidStatus, src.nfcStatus) ? lang.failed : "") +
            " " +
            prettifyTransactionType(src.type) +
            " " +
            lang.voided,
        });
      }

      const pushPaymentRow = (baseRow) => {
        if (type === TransactionType.MIXED) {
          data.push({
            ...baseRow,
            status:
              (idFailed ? lang.failed : "") + " " + prettifyTransactionType(TransactionType.SALE),
          });
          data.push({
            ...baseRow,
            status:
              (idFailed ? lang.failed : "") + " " + prettifyTransactionType(TransactionType.REDEEM),
          });
          return;
        }

        data.push({
          ...baseRow,
          status: (idFailed ? lang.failed : "") + " " + prettifyTransactionType(type),
        });
      };

      if (sortedDetails.length > 0) {
        sortedDetails.forEach((detail) => {
          pushPaymentRow({
            paymentOrder: formatNumberToOrdinal(detail.paymentOrder),
            dateTime,
            staff: staffRow,
            guest: {
              name: detail.guestName || "-",
              tagUid: detail.tagUid,
            },
          });
        });
      } else {
        pushPaymentRow({
          paymentOrder: "-",
          dateTime,
          staff: staffRow,
          guest: {
            name: "-",
            tagUid: null,
          },
        });
      }

      return {
        data,
        columns: [
          {
            text: lang.paymentOrder,
            key: "paymentOrder",
          },
          {
            text: lang.date,
            key: "dateTime",
            custom: true,
          },
          {
            text: lang.staff,
            key: "staff",
            custom: true,
          },
          {
            text: lang.type,
            key: "status",
          },
          {
            text: lang.guest,
            key: "guest",
            custom: true,
          },
        ],
      };
    },
  },
  soarSummary: {
    transform: ({ src }) => {
      const pricingModel = src.pricingModel;
      if (!pricingModel) {
        return null;
      }

      const transactionItems = src.transactionItems || [];
      const cartDiscount = src.cartDiscount || {};

      const formatPercentage = (value) => {
        if (value === null || value === undefined || value === "") {
          return "";
        }
        return `${parseFloat(value)}%`;
      };

      const hasItemDiscount = transactionItems.some(
        (item) => item.transactionDiscountType === "ITEM_DISCOUNT" && item.discountAmount,
      );
      const hasCartDiscount = transactionItems.some(
        (item) => item.transactionDiscountType === "CART_DISCOUNT" && item.discountAmount,
      );
      // "Per item" (ITEM_DISCOUNT) and "Whole check" (CART_DISCOUNT) are mutually exclusive
      // discount modes set on the backend per transaction; ITEM_DISCOUNT takes precedence
      // if both were ever present.
      const discountMode = hasItemDiscount
        ? "ITEM_DISCOUNT"
        : hasCartDiscount
          ? "CART_DISCOUNT"
          : null;

      const rows = [];
      (pricingModel.items || []).forEach((priceItem, index) => {
        const transactionItem = transactionItems[index] || {};
        const name = transactionItem.itemName || transactionItem.productName || "-";
        const unitPrice = formatNumberToMoneyWithCurrencySymbol(priceItem.priceBeforeTax);
        const taxPercentage = formatPercentage(priceItem.tax);
        rows.push({
          key: `item-${transactionItem.transactionItemId || index}`,
          kind: "item",
          name,
          note: transactionItem.note || null,
          quantity: priceItem.quantity,
          unitPrice,
          taxPercentage,
          amount: formatNumberToMoneyWithCurrencySymbol(priceItem.totalPriceBeforeTax),
        });

        if (discountMode === "ITEM_DISCOUNT" && transactionItem.transactionDiscountType) {
          const discountQuantity = transactionItem.discountQuantity || 1;
          const perUnitAmount = formatNumberToMoneyWithCurrencySymbol(
            divide(transactionItem.discountAmount, discountQuantity),
          );
          rows.push({
            key: `item-discount-${transactionItem.transactionItemId || index}`,
            kind: "itemDiscount",
            name:
              transactionItem.discountType === "FIXED"
                ? lang.populate(lang.itemDiscountFixedLabel, [
                    transactionItem.discountName || lang.discount,
                  ])
                : lang.populate(lang.itemDiscountLabel, [
                    transactionItem.discountName || lang.discount,
                    parseFloat(transactionItem.discountPercentage || 0),
                  ]),
            taxPercentage,
            amount: lang.populate(lang.itemDiscountAmountText, [discountQuantity, perUnitAmount]),
          });
        }

        (transactionItem.transactionVoucherItems || []).forEach((voucherItem, voucherIndex) => {
          rows.push({
            key: `item-voucher-${transactionItem.transactionItemId || index}-${
              voucherItem.transactionVoucherItemId || voucherIndex
            }`,
            kind: "voucher",
            name: lang.populate(lang.voucherItemName, [
              voucherItem.quantity,
              voucherItem.itemName || name,
            ]),
            quantity: voucherItem.quantity,
            voucherUnitPrice: formatNumberToMoneyWithCurrencySymbol(voucherItem.voucherValue),
            taxPercentage,
            amount: formatNumberToMoneyWithCurrencySymbol(0),
          });
        });
      });

      const discountAmount = pricingModel.discountAmount;

      return {
        discountMode,
        rows,
        location: src.location,
        table: src.table,
        subtotal: formatNumberToMoneyWithCurrencySymbol(pricingModel.originalSubTotal),
        serviceChargePercentage: formatPercentage(pricingModel.serviceCharge),
        serviceChargeAmount: formatNumberToMoneyWithCurrencySymbol(
          pricingModel.serviceChargeAmount,
        ),
        hasVoucher: Boolean(pricingModel.appliedVoucher),
        voucherAmount: formatNumberToMoneyWithCurrencySymbol(pricingModel.appliedVoucher),
        hasDiscount: Boolean(discountAmount),
        discountLabel:
          discountMode === "CART_DISCOUNT"
            ? cartDiscount.discountType === "FIXED"
              ? lang.populate(lang.discountWithName, [
                  lang.discount,
                  cartDiscount.discountName || lang.discount,
                ])
              : lang.populate(lang.discountWithNamePercentage, [
                  lang.discount,
                  cartDiscount.discountName || lang.discount,
                  parseFloat(cartDiscount.discountPercentage || 0),
                ])
            : lang.discount,
        discountAmount: formatNumberToMoneyWithCurrencySymbol(discountAmount),
        netValue: formatNumberToMoneyWithCurrencySymbol(pricingModel.netSales),
        taxBaseAmount: formatNumberToMoneyWithCurrencySymbol(pricingModel.taxBaseAmount),
        taxAmount: formatNumberToMoneyWithCurrencySymbol(pricingModel.taxAmount),
        tipAmount: formatNumberToMoneyWithCurrencySymbol(pricingModel.tipAmount),
        totalDue: formatNumberToMoneyWithCurrencySymbol(pricingModel.grandTotal),
      };
    },
  },
  soarPaymentDetails: {
    transform: ({ src }) => {
      const details = src.transactionPaymentDetails || [];
      if (!details.length) {
        return null;
      }
      const money = formatNumberToMoneyWithCurrencySymbol;
      const num = (value) => Number(value || 0);
      const round2 = (value) => Math.round(value * 100) / 100;
      const cell = (value, prefix = "") => ({
        text: `${Math.abs(value) >= 0.005 ? prefix : ""}${money(value)}`,
        isZero: Math.abs(value) < 0.005,
      });
      const balanceOf = (credits, freeCredits) => {
        const c = num(credits);
        return {
          credits: money(c < 0 ? 0 : c),
          freeCredits: money(num(freeCredits)),
          postpaidCreditsUsed: money(c < 0 ? Math.abs(c) : 0),
        };
      };
      const { firstName, lastName } = src.staff || {};
      const staff = {
        name: formatName(firstName, lastName) || "-",
        tagUid: src.staffTagUid || "",
      };
      const totals = {
        paidAmount: 0,
        fromCredit: 0,
        fromFreeCredit: 0,
        byVoucher: 0,
        addFreeCredit: 0,
      };

      const rows = [...details]
        .sort((a, b) => (a.paymentOrder || 0) - (b.paymentOrder || 0))
        .map((detail) => {
          const paidAmount = num(detail.originalAmount);
          const fromCredit = num(detail.paidCredits) + num(detail.paidPostpaidCredits);
          const fromFreeCredit = num(detail.paidFreeCredits);
          const vouchers = detail.vouchers || [];
          const voucherValue = (v) => num(v.voucherAmount) * (num(v.quantity) || 1);
          const byVoucher = round2(num(detail.paidByVoucher) + num(detail.paidByVoucherTax));
          const voucherBase = vouchers.reduce((sum, v) => sum + voucherValue(v), 0);
          let allocated = 0;
          const voucherShare = (v, index) => {
            if (index === vouchers.length - 1) {
              return round2(byVoucher - allocated);
            }
            const share = voucherBase ? round2((voucherValue(v) / voucherBase) * byVoucher) : 0;
            allocated = round2(allocated + share);
            return share;
          };
          const addFreeCredit = num(detail.addFreeCredit);

          totals.paidAmount += paidAmount;
          totals.fromCredit += fromCredit;
          totals.fromFreeCredit += fromFreeCredit;
          totals.byVoucher += byVoucher;
          totals.addFreeCredit += addFreeCredit;

          const d = detail.createdDate ? timeAgo(detail.createdDate, true) : {};
          return {
            id: detail.id || detail.paymentOrder,
            order: formatNumberToOrdinal(detail.paymentOrder),
            guestName: detail.guestName || "-",
            tagUid: detail.tagUid || "",
            tap:
              detail.tagUseCount !== null && detail.tagUseCount !== undefined
                ? lang.populate(lang.tapNumber, [detail.tagUseCount])
                : "",
            dateTime: [d.date, d.time].filter(Boolean).join(", ") || "-",
            paidAmount: cell(paidAmount),
            fromCredit: cell(fromCredit),
            fromFreeCredit: cell(fromFreeCredit),
            byVoucher: cell(byVoucher),
            addFreeCredit: cell(addFreeCredit, "+"),
            staff,
            previousBalance: balanceOf(
              detail.userTagPreviousBalance,
              detail.userTagPreviousFreeCredits,
            ),
            totalPaid: {
              credits: money(num(detail.paidCredits)),
              freeCredits: money(fromFreeCredit),
              postpaidCreditsUsed: money(num(detail.paidPostpaidCredits)),
            },
            vouchers: vouchers.map((v, index) => ({
              label: `${v.quantity}x ${v.voucherName}`,
              amount: money(voucherShare(v, index)),
            })),
            paidByAppliedVoucher: money(byVoucher),
            returnedFreeCredit: cell(addFreeCredit, "+"),
            newBalance: balanceOf(detail.userTagNewBalance, detail.userTagNewFreeCredits),
          };
        });

      // The bill to pay is rebuilt from pricingModel instead of `originalAmount`, which is
      // the pre-discount amount and therefore does not match what the guests actually owe.
      const pricingModel = src.pricingModel;
      const totalBillToPay = pricingModel
        ? round2(
            num(pricingModel.originalSubTotal) -
              num(pricingModel.discountAmount) +
              num(pricingModel.serviceChargeAmount) +
              num(pricingModel.tipAmount) +
              num(pricingModel.subTotalTaxAmount) +
              num(pricingModel.serviceChargeTaxAmount) -
              num(pricingModel.discountTaxAmount),
          )
        : num(src.originalAmount);

      return {
        rows,
        summary: {
          totalBill: money(totalBillToPay),
          splitCount: details.length,
          totalCollected: money(round2(totals.paidAmount - totals.addFreeCredit)),
        },
        total: {
          paidAmount: cell(totals.paidAmount),
          fromCredit: cell(totals.fromCredit),
          fromFreeCredit: cell(totals.fromFreeCredit),
          byVoucher: cell(totals.byVoucher),
          addFreeCredit: cell(totals.addFreeCredit, "+"),
        },
      };
    },
  },
};
